import { InputRule } from '@tiptap/core';
import {
  applyNonLeadingSectionInputRule,
  applySectionInputRule,
  SECTION_HEADING_PATTERN,
  SECTION_NON_LEADING_HEADING_PATTERN,
} from './apply-section-input-rule';

/**
 * The one creation method these rules can report. The kernel owns this literal
 * (it describes the rule's own mechanism); the app-side analytics adapter maps
 * it into the product `section_created` vocabulary.
 */
export type SectionInputRuleCreationMethod = 'heading_shortcut';

/**
 * Live `# ` → section conversion for the markdown-native editor. A standalone
 * twin of the vMarkdown rules of the same name: both wrappers delegate to the
 * shared transforms, so the two editors never import each other's extensions.
 *
 * Two rules: the anchored `# ` at a paragraph start, and a non-leading rule
 * that splits at the exact inline caret (including after a soft/hard break).
 * See those handlers for the conversion behaviour.
 *
 * On a successful conversion either rule reports exactly one section creation
 * through `onSectionCreated`. The rules run in ProseMirror (outside React) and
 * carry no analytics themselves — the host supplies the dispatch (the
 * `MarkdownSectionNode` `onSectionCreated` option, wired by the extension kit
 * to the app-side section-analytics adapter).
 */
export function createSectionInputRules(
  onSectionCreated: (creationMethod: SectionInputRuleCreationMethod) => void = () => {}
): InputRule[] {
  type SectionRuleHandler = typeof applySectionInputRule;
  const withTracking =
    (handler: SectionRuleHandler): SectionRuleHandler =>
    (props) => {
      const result = handler(props);
      // `null` means the line wasn't convertible — no section was created.
      if (result === null) return result;

      onSectionCreated('heading_shortcut');
      return result;
    };

  return [
    new InputRule({
      find: SECTION_HEADING_PATTERN,
      handler: withTracking(applySectionInputRule),
    }),
    new InputRule({
      find: SECTION_NON_LEADING_HEADING_PATTERN,
      handler: withTracking(applyNonLeadingSectionInputRule),
    }),
  ];
}
